"use client"
import { useState, useEffect } from "react"
import Hero from "@/components/home/hero"
import Features from "@/components/features"
import { TestimonialsSection } from "@/components/testimonials"
import { NewReleasePromo } from "@/components/new-release-promo"
import { FAQSection } from "@/components/faq-section"
import { PricingSection } from "@/components/pricing-section"
import { StickyFooter } from "@/components/sticky-footer"
import { Button } from "@/components/ui/button"
import { Menu } from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import { supabase } from "@/lib/supabase"
import { useNavigate } from "react-router-dom"
import { loadStripe } from '@stripe/stripe-js'

const amplyLogo = new URL('../assets/amply.png', import.meta.url).href
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)

const Pricing = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [plans, setPlans] = useState([])
  const FREE_PLAN_ID = '6d4ecf79-200c-4efc-85a9-e1205386d362'
  const [subscriptionPlanId, setSubscriptionPlanId] = useState(FREE_PLAN_ID)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  useEffect(() => {
    const root = window.document.documentElement
    root.classList.remove("light", "system")
    root.classList.add("dark")
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 100)
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Load subscription plans
  useEffect(() => {
    supabase
      .from('plans')
      .select('*')
      .then(({ data, error }) => {
        if (error) console.error('Error loading plans:', error)
        else setPlans(data)
      })
  }, [])
  
  // Load current user's subscription
  useEffect(() => {
    if (!user) return // no user: keep free plan as owned
    supabase
      .from('subscriptions')
      .select('plan_id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) console.error('Error loading subscription:', error)
        setSubscriptionPlanId(data?.plan_id || FREE_PLAN_ID)
      })
  }, [user])

  // Subscribe to a plan: open Stripe Checkout Session
  const handleSubscribe = async (plan) => {
    if (!user) {
      navigate('/login')
      return
    }
    // Free plan: if not logged in, send user to register; if logged in, upsert free subscription
    if (plan.price_cents === 0) {
      if (!user) {
        navigate('/register')
        return
      }
      // Upsert free subscription
      try {
        const { error: freeErr } = await supabase
          .from('subscriptions')
          .upsert(
            { user_id: user.id, plan_id: plan.id, status: 'active' },
            { onConflict: 'user_id' }
          )
        if (freeErr) {
          console.error('Free subscription error:', freeErr)
        } else {
          alert('Subscribed to free plan!')
          navigate('/settings')
        }
      } catch (err) {
        console.error('Error upserting free subscription:', err)
      }
      return
    }
    // Paid plan: call backend to create session (and upsert subscription)
    try {
      console.log('Requesting checkout session for plan:', plan)
      const traceId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
  const response = await fetch('https://backend.amply.buildfast.us/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priceId: plan.stripe_price_id, userId: user.id, ref: localStorage.getItem('referrerId') || '', traceId }),
      })
      const { url, error } = await response.json()
      if (error) {
        console.error('Checkout session error:', error)
        alert(error)
        return
      }
      // Redirect to Stripe Checkout
      window.location.href = url
    } catch (err) {
      console.error('Network error creating checkout session:', err)
    }
  }

  return (
  <div className="w-full relative text-white">
      {/* Pearl Mist Background with Top Glow */}
      <div
        className="absolute inset-0 z-0 bg-[#000000]"
        style={{
          background: "radial-gradient(ellipse 50% 35% at 50% 0%, rgba(0,0,0,0.8), rgba(255,255,255,0.1) 60%), #000",
        }}
      />

      {/* Desktop Header */}
      <header
        className={`sticky top-4 z-[9999] mx-auto hidden w-full flex-row items-center justify-between self-start rounded-full bg-transparent md:flex backdrop-blur-sm border border-[#ebcebc] shadow-lg transition-all duration-300 ${
          isScrolled ? "max-w-3xl px-2" : "max-w-5xl px-4"
        } py-2`}
        style={{
          willChange: "transform",
          transform: "translateZ(0)",
          backfaceVisibility: "hidden",
          perspective: "1000px",
        }}
      >
        <a
          className={`z-50 flex items-center justify-center gap-2 transition-all duration-300 ${
            isScrolled ? "ml-4" : ""
          }`}
          href="/"
        >
                <img 
                src={amplyLogo}
                className="w-8 h-8"
                alt="Amply Logo"
                />
        </a>

  <div className="absolute inset-0 hidden flex-1 flex-row items-center justify-center space-x-2 text-sm font-medium text-white transition duration-200 hover:text-white md:flex md:space-x-2">
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="./#features"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Features</span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/about"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">About</span>
          </a>
          <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/pricing"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Pricing</span>
          </a>
                  <a
            className="relative px-4 py-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            href="/contact"
          >
            <span style={{fontWeight: 'bold'}} className="relative z-20">Contact Us</span>
          </a>
        </div>

        <div className="flex items-center gap-4">
          {user ? (
            <a
              href="/dashboard"
              className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
            >
              Dashboard
            </a>
          ) : (
            <>
              <a
                href="/login"
                className="rounded-full border border-[#e3c5b3] font-bold relative cursor-pointer transition duration-200 inline-block text-center text-primary-foreground px-4 py-2 text-sm"
              >
                Log In
              </a>
              <a
                href="/register"
                className="rounded-full font-bold relative cursor-pointer transition duration-300 inline-block text-center bg-[#e3c5b3] text-white shadow-lg hover:shadow-xl hover:scale-105 px-6 py-3 text-base"
              >
                Sign Up
              </a>
            </>
          )}
        </div>
      </header>

      {/* Mobile Header */}
  <header className="sticky top-4 z-[9999] mx-4 flex w-auto flex-row items-center justify-between rounded-full bg-transparent backdrop-blur-sm border border-[#ebcebc] shadow-lg md:hidden px-4 py-3">
        <a
          className="flex items-center justify-center gap-2"
          href="./"
        >
                <img 
                src={amplyLogo}
                className="w-8 h-8"
                alt="Amply Logo"
                />
        </a>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-background/50 border border-border/50 transition-colors hover:bg-background/80"
          aria-label="Toggle menu"
        >
          <div className="flex flex-col items-center justify-center w-5 h-5 space-y-1">
            {isMobileMenuOpen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5 text-foreground"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
             <Menu className="w-5 h-5 text-foreground" />
            )}
          </div>
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
  <div className="fixed inset-0 z-[9998] bg-transparent backdrop-blur-sm md:hidden">
    <div className="absolute top-28 left-4 right-4 bg-transparent/80 backdrop-blur-md border border-border/50 rounded-2xl shadow-2xl p-6">
      <nav className="flex flex-col space-y-4">
        <a
            href="./#features"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Features
        </a>
        <a
          href="/about"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          About
        </a>
        <a
          href="/pricing"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Pricing
        </a>
       <a
          href="/contact"
          className="text-left px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50"
        >
          Contact Us
        </a>
        <div className="border-t border-border/50 pt-4 mt-4 flex flex-col space-y-3">
          {user ? (
            <a
              href="/dashboard"
              className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
            >
              Dashboard
            </a>
          ) : (
            <>
              <a
                href="/login"
                className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
              >
                Log In
              </a>
              <a
                href="/register"
                className="px-4 py-3 text-lg font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-background/50 cursor-pointer"
              >
                Sign Up
              </a>
            </>
          )}
        </div>
      </nav>
    </div>
  </div>
)}
      {/* Pricing Section */}
  <div id="pricing">
      <PricingSection plans={plans} />
      </div>

      <div className="h-[500px]"/>
      <StickyFooter />
    </div>
  )
}

export default Pricing;
